import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySharedService } from '@amfam/policy/shared';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CommunicationService } from '@amfam/shared/utility/shared-services';

import { QuickQuoteDataService } from '../../services/quick-quote-data.service';
import * as quickQuoteDataActions from './quick-quote-data.actions';

@Injectable()
export class QuickQuoteDataEffects {
  quickQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.getQuickQuote),
      map(action => this.quickQuoteDataService.mapQuickQuoteRequest(action.quickQuoteRequest)),
      switchMap(quickQuote =>
        this.quickQuoteDataService
          .getQuickQuote(
            quickQuote.policyNumber,
            quickQuote.apiPayload,
            quickQuote.apiPayload.operation
          )
          .pipe(
            switchMap(quote => {
              const plpcEnabled = this.featureFlagService.isEnabled(
                'replace_vehicle_plpc_migration'
              );
              return quickQuote.apiPayload.operation === 'quickquotes'
                ? of(quickQuoteDataActions.getQuickQuoteSuccess({ response: quote }))
                : this.policySharedService
                    .getFuturePayments(
                      {
                        policyNumber: quickQuote.policyNumber,
                        submissionId: quote.premium.workOrderNumber
                      },
                      plpcEnabled
                    )
                    .pipe(
                      switchMap(futurePayments =>
                        this.mapQuoteAndFutrePayments(quote, futurePayments)
                      ),
                      catchError(() =>
                        of(quickQuoteDataActions.getQuoteSuccess({ response: quote }))
                      )
                    );
            }),
            catchError(error =>
              of(quickQuoteDataActions.getQuickQuoteFailed({ error: error.status }))
            )
          )
      )
    )
  );

  getQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.getQuote),
      map(action => action.quoteRequest),
      switchMap(quickQuote =>
        this.quickQuoteDataService.getQuotePLPC(quickQuote).pipe(
          map(plpcReplaceVehicleRes => {
            return this.quickQuoteDataService.mapPremiumEstimate(plpcReplaceVehicleRes);
          }),
          switchMap(quote => {
            const plpcEnabled = this.featureFlagService.isEnabled('replace_vehicle_plpc_migration');
            return this.policySharedService
              .getFuturePayments(
                {
                  policyNumber: quickQuote.policyNumber,
                  submissionId: quote?.result?.submissionId
                },
                plpcEnabled
              )
              .pipe(
                switchMap(futurePayments => [
                  quickQuoteDataActions.getQuoteSuccess({ response: quote }),
                  quickQuoteDataActions.loadFuturePaymentsSuccess({
                    paymentSchedule: futurePayments?.result?.paymentSchedule?.paymentPreviewItems
                  })
                ]),
                catchError(() => of(quickQuoteDataActions.getQuoteSuccess({ response: quote })))
              );
          }),
          catchError(error =>
            of(quickQuoteDataActions.getQuickQuoteFailed({ error: error.status }))
          )
        )
      )
    )
  );
  customerEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        quickQuoteDataActions.quickQuoteSendCustomerEmail,
        quickQuoteDataActions.quickQuoteSendToAgentCustomerEmail,
        quickQuoteDataActions.quickQuoteEmailCustomerOnError,
        quickQuoteDataActions.replaceVehicleCustomerEmailOnCancelProcess,
        quickQuoteDataActions.emailCustomerOnBind
      ),
      mergeMap(action =>
        this.communicationService.sendTemplatedEmail(action.email).pipe(
          map(() => this.quickQuoteDataService.mapCustomerEmailSuccessAction(action.type)),
          catchError(error =>
            this.quickQuoteDataService.mapCustomerEmailFailureAction(action.type, error)
          )
        )
      )
    )
  );

  agentEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        quickQuoteDataActions.quickQuoteSendToAgentEmail,
        quickQuoteDataActions.quickQuoteEmailAgentOnError,
        quickQuoteDataActions.replaceVehicleAgentEmailOnCancelProcess,
        quickQuoteDataActions.emailAgentOnBind
      ),
      mergeMap(action =>
        this.communicationService.sendInternalEmail(action.email).pipe(
          map(() => this.quickQuoteDataService.mapAgentEmailSuccessAction(action.type)),
          catchError(error =>
            this.quickQuoteDataService.mapAgentEmailFailureAction(action.type, error.status)
          )
        )
      )
    )
  );

  ansNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        quickQuoteDataActions.ansNotification,
        quickQuoteDataActions.ansNotificationOnError,
        quickQuoteDataActions.replaceVehicleANSOnCancelProcess,
        quickQuoteDataActions.ansNotificationOnBind
      ),
      switchMap(action =>
        this.communicationService.ansNotification(action.notification).pipe(
          map(() => this.quickQuoteDataService.mapAnsSuccessAction(action.type)),
          catchError(error =>
            of(this.quickQuoteDataService.mapAnsFailureAction(action.type, error.status))
          )
        )
      )
    )
  );

  bindQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.bindQuickQuote),
      map(action => action.payload),
      switchMap(payload =>
        this.quickQuoteDataService.submitOnlineBind(payload).pipe(
          map(response => quickQuoteDataActions.bindQuickQuoteSuccess({ response })),
          catchError(error =>
            of(quickQuoteDataActions.bindQuickQuoteFail({ status: error.status }))
          )
        )
      )
    )
  );

  bindQuotePLPC$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.bindQuote),
      map(action => action.payload),
      withLatestFrom(this.store.select(vehicleQuery.selectCriteria)),
      switchMap(([payload, criteria]) =>
        this.quickQuoteDataService.submitOnlineBindPLPC(payload, criteria.vin).pipe(
          switchMap(response => [
            quickQuoteDataActions.bindQuoteSuccess({ response }),
            quickQuoteDataActions.notfiyOnBindQuoteSuccess({ payload }),
            quickQuoteDataActions.bindQuoteSuccessAnalytics({
              payload: { workOrderNumber: payload.workOrderNumber, vehicleId: payload.vehicleId }
            })
          ]),
          catchError(error => of(quickQuoteDataActions.bindQuoteFail({ status: error.status })))
        )
      )
    )
  );

  bindQuoteAnalytics$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(quickQuoteDataActions.bindQuoteSuccessAnalytics),
        map(action => action.payload),
        withLatestFrom(this.store.select(vehicleQuery.selectNewVehicle)),
        tap(([payload, newVehicle]) => {
          this.analyticsFacade.trackEvent({
            event: 'replace_vehicle_complete',
            form_name: 'Replace Vehicle Flow',
            vehicle_make: newVehicle.make,
            vehicle_model: newVehicle.model,
            vehicle_year: newVehicle.year,
            quote_id: payload.workOrderNumber,
            product_line: 'auto'
          });
        })
      ),
    {
      dispatch: false
    }
  );

  notifyAgentOnBindSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.notfiyOnBindQuoteSuccess),
      map(action => this.quickQuoteDataService.prepareNotify(action.payload)),
      switchMap(({ ans, email, customerEmail }) => [
        quickQuoteDataActions.ansNotificationOnBind({ notification: ans }),
        quickQuoteDataActions.emailAgentOnBind({ email }),
        quickQuoteDataActions.emailCustomerOnBind({ email: customerEmail })
      ])
    )
  );

  getFuturePayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quickQuoteDataActions.loadFuturePayments),
      switchMap(payload => {
        const plpcEnabled = this.featureFlagService.isEnabled('replace_vehicle_plpc_migration');
        return this.policySharedService.getFuturePayments(payload, plpcEnabled).pipe(
          map(response => this.mapFuturePayments(response)),
          catchError(() => of(quickQuoteDataActions.loadFuturePaymentsFailed()))
        );
      })
    )
  );

  private mapFuturePayments(response) {
    return quickQuoteDataActions.loadFuturePaymentsSuccess({
      paymentSchedule: response?.result?.paymentSchedule?.paymentPreviewItems
    });
  }

  private mapQuoteAndFutrePayments(quote, futurePayments) {
    return [
      quickQuoteDataActions.getQuoteSuccess({ response: quote }),
      this.mapFuturePayments(futurePayments)
    ];
  }

  constructor(
    private actions$: Actions,
    private quickQuoteDataService: QuickQuoteDataService,
    private communicationService: CommunicationService,
    private policySharedService: PolicySharedService,
    private featureFlagService: FeatureFlagService,
    private store: Store,
    private analyticsFacade: AnalyticsFacade
  ) {}
}
